import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Command, CommandInput, CommandList, Skeleton } from "../ui";
import { CommandIcon, Search } from "lucide-react";
import { useStudentSearch } from "@/hooks/useStudentSearch";
import { CommandLoading } from "cmdk";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/Dialog";
import { SearchResult } from "./SearchResult";

const SHORTCUT_KEY = "k";

export function StudentSearch() {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<string>();

  const { students, loading, error } = useStudentSearch({
    search,
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === SHORTCUT_KEY && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        setSearch(undefined);
      }}
    >
      <DialogHeader className="sr-only">
        <DialogTitle>{t("search.description")}</DialogTitle>
        <DialogDescription>{t("search.prompt")}</DialogDescription>
      </DialogHeader>
      <DialogTrigger asChild>
        <Button
          className="flex w-84 gap-1 rounded-full bg-slate-50 text-muted-foreground"
          variant="ghost"
        >
          <Search />
          <span className="grow text-left">{t("search.button")}</span>
          {navigator.userAgent.includes("Mac") ? (
            <CommandIcon className="flex size-5 items-center rounded border bg-muted p-0.5 font-mono font-medium text-muted-foreground opacity-100" />
          ) : (
            <kbd className="flex h-5 justify-center rounded border bg-muted px-1 font-mono font-medium text-muted-foreground opacity-100">
              <span className="text-sm">{t("global.controlKey")}</span>
            </kbd>
          )}
          <kbd className="flex size-5 justify-center rounded border bg-muted font-mono font-medium text-muted-foreground opacity-100">
            <span className="text-sm">{SHORTCUT_KEY.toUpperCase()}</span>
          </kbd>
        </Button>
      </DialogTrigger>
      <DialogContent className="top-[25%] translate-y-[0%] overflow-hidden p-0">
        <Command
          shouldFilter={false}
          className="**:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5"
        >
          <CommandInput
            onValueChange={setSearch}
            placeholder={t("search.prompt")}
            className="py-3"
          />
          {search?.length ? (
            <CommandList>
              {loading ? (
                <LoadingSkeleton />
              ) : (
                <SearchResult
                  students={students}
                  error={error}
                  setOpen={setOpen}
                  profileSource="Search"
                />
              )}
            </CommandList>
          ) : (
            <></>
          )}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function LoadingSkeleton() {
  return (
    <CommandLoading>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <Skeleton className="size-6 rounded-full" />
        <Skeleton className="h-4 w-full" />
      </div>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <Skeleton className="size-6 rounded-full" />
        <Skeleton className="h-4 w-full" />
      </div>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <Skeleton className="size-6 rounded-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    </CommandLoading>
  );
}
